import { callApi } from '../utils';

// Wallet overdraft facility — a credit line on a customer's wallet that lets
// them spend past a zero balance up to a cap set by FINANCE.
//
// The debt IS the negative balance: a wallet at `balance: -1500` owes ₦1,500.
// There is no separate debt ledger, and the debt is repaid automatically the
// next time the wallet is credited.
//
// All amounts crossing this boundary are in naira (the database stores kobo;
// the server has already converted) and may be fractional to 2dp.
//
// Requests go through the interceptor-enabled `callApi` so writes pick up the
// global approval-required (maker-checker) and 2fa-required handling.

export const REASON_MAX = 500;

// The four facility states the panel must distinguish.
export const FACILITY_STATE = {
  NONE: 'NONE', // no facility at all
  IN_CREDIT: 'IN_CREDIT', // has a facility, not drawing on it
  DRAWING: 'DRAWING', // owes money, still within the limit
  OVER_LIMIT: 'OVER_LIMIT', // owes more than the limit — spending is blocked
};

const num = (value) => {
  const n = Number(value);
  return isFinite(n) ? n : 0;
};

// `GET …/wallet` splits its response: `data` carries the wallet's stored
// columns, `meta` carries everything derived from them. A projection is not
// resource state, which is why there is no separate overdraft or summary
// endpoint to call — one wallet read has all of it.
//
// `PATCH …/wallet/overdraft` mirrors the same split and adds `previous_limit` /
// `performed_by`, so the panel can be refreshed straight from the write.
//
// The customer surfaces (`GET /client/me/wallet`, `GET /mobile/me/wallet`)
// return an identical `meta` block — their `data` carries `number` and no
// `threshold` — so this is the one adapter for all three.
//
// Nothing is re-derived from `data` here. The server owns these figures; if
// `meta` were missing we would rather render zeroes than invent numbers that
// disagree with the ones spending decisions are actually made against.
export const normaliseWallet = (envelope = {}) => {
  const data = (envelope && envelope.data) || {};
  const meta = (envelope && envelope.meta) || {};
  const overdraft = meta.overdraft || {};

  return {
    // Stored columns. `balance` is negative while the customer is in debt.
    balance: num(data.balance),
    limit: num(data.overdraft_limit),
    threshold: data.threshold == null ? null : num(data.threshold),
    number: data.number == null ? null : data.number, // customer surfaces only
    updatedAt: data.updated_at || null,

    // Derived figures.
    spendingPower: num(meta.spending_power),
    enabled: !!overdraft.enabled, // a facility is granted (limit > 0)
    used: num(overdraft.used), // outstanding debt — what they owe
    available: num(overdraft.available), // headroom left on the facility
    active: !!overdraft.active, // currently drawing on it (balance < 0)
    overLimit: !!overdraft.over_limit, // drawn past the cap — spending blocked

    // Present on the PATCH response only.
    previousLimit:
      meta.previous_limit == null ? null : num(meta.previous_limit),
    performedBy: meta.performed_by == null ? null : meta.performed_by,
  };
};

// Over-limit is checked first: FINANCE can legitimately drop a limit below what
// is already drawn, which leaves the customer both "no facility" (limit 0) and
// blocked from spending. Blocked is the more important thing to surface.
export const facilityState = (wallet) => {
  if (!wallet) return FACILITY_STATE.NONE;
  if (wallet.overLimit) return FACILITY_STATE.OVER_LIMIT;
  if (wallet.active) return FACILITY_STATE.DRAWING;
  if (wallet.limit <= 0) return FACILITY_STATE.NONE;
  return FACILITY_STATE.IN_CREDIT;
};

// Client-side guard for the set-limit form. The server validates too; this just
// saves the operator a round trip. Returns a message, or null when valid.
export const validateLimit = (raw) => {
  const value = String(raw == null ? '' : raw).trim();

  if (!value) return 'Enter a limit.';
  if (!/^\d+(\.\d{1,2})?$/.test(value)) {
    return 'Enter a positive amount in naira, to at most 2 decimal places.';
  }
  if (!isFinite(Number(value))) return 'Enter a valid amount.';

  return null;
};

export const validateReason = (raw) => {
  const value = String(raw == null ? '' : raw);
  if (value.length > REASON_MAX) {
    return `Reason must be ${REASON_MAX} characters or fewer.`;
  }
  return null;
};

// Server `400 validation-error` bodies carry `errors: [{ field, message }]`.
// Fold them into the `{ field: message }` shape the form renders against.
export const toFieldErrors = (errors = []) => {
  if (!Array.isArray(errors)) return {};

  return errors.reduce((acc, e) => {
    if (e && e.field) acc[e.field] = e.message || 'Invalid value.';
    return acc;
  }, {});
};

// A denied `user-wallet:update` grant is a 403 and a rejected field is a 400 —
// both used to arrive as a 500, which is why nothing keys on status 500 here.
export const isApprovalRequired = (err) =>
  !!err &&
  typeof err === 'object' &&
  (err.__approvalHandled === true || err.status === 'approval-required');

// GET /admin/users/:user_id/wallet
//
// The only overdraft read. There is no `…/wallet/overdraft` GET and no
// `…/wallet/summary`; the figures ride in this response's `meta`.
export const getWallet = (userId) =>
  callApi(`/users/${userId}/wallet`).then(normaliseWallet);

// PATCH /admin/users/:user_id/wallet/overdraft
//
// Still the only write resource for sizing the facility. Resolves with the full
// envelope: it mirrors `GET …/wallet` plus `meta.previous_limit` /
// `meta.performed_by`, so the caller can refresh from it — but a 2xx does not
// necessarily mean the change was applied, see the maker-checker note in the
// modal.
export const setOverdraftLimit = (userId, { limit, reason }) => {
  const body = { limit: Number(limit) };
  const trimmed = (reason || '').trim();
  if (trimmed) body.reason = trimmed;

  return callApi(`/users/${userId}/wallet/overdraft`, body, 'patch');
};

// GET /admin/users/:user_id/wallet/overdraft/history — newest first.
//
// The audit trail of limit changes, distinct from the payment-note history in
// paymentNote.service.js. Failure here is non-fatal: the panel renders without
// it rather than blocking on it.
export const getOverdraftHistory = (userId, { page = 1, limit = 30 } = {}) =>
  callApi(`/users/${userId}/wallet/overdraft/history`, { page, limit }).then(
    ({ data = [], meta = {} }) => ({ rows: data, meta })
  );

export default {
  getWallet,
  setOverdraftLimit,
  getOverdraftHistory,
};
